import { useState } from "react"
import {
  Form,
  Button,
  Modal,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap"
import useFetch from "../../../_fetch"

interface Product {
  name: string
  ean?: string
}

interface Props {
  onProductsProcessed: (products: Product[]) => void
}

export default function ProductFetcher({ onProductsProcessed }: Props) {
  const { doFetch } = useFetch()
  const [showModal, setShowModal] = useState(false)
  const [foundProducts, setFoundProducts] = useState<Product[]>([])
  const [modalError, setModalError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validNameCount = foundProducts
    .map((p) => p.name.trim())
    .filter(Boolean).length

  const handleOpenModal = () => {
    setShowModal(true)
    setFoundProducts([{ name: "" }])
    setModalError(null)
  }

  const handleChangeProductName = (index: number, newName: string) => {
    const updated = [...foundProducts]
    updated[index].name = newName
    setFoundProducts(updated)
  }

  const handleDeleteProduct = (index: number) => {
    setFoundProducts((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [{ name: "" }]
    })
  }

  const handleAddProduct = () => {
    setFoundProducts([...foundProducts, { name: "" }])
  }

  const handleSubmitProducts = async () => {
    setIsSubmitting(true)
    setModalError(null)

    try {
      const names = foundProducts.map((p) => p.name.trim()).filter((n) => n)
      if (names.length === 0) throw new Error("Ingen produktnavne at sende.")

      const res = await doFetch('/eb-pristjek/get-products-by-name', {
        productNames: names,
      })

      if(res.status >= 400) throw new Error(await res.text())
      
      const products: Product[] = await res.json()

      onProductsProcessed(products)
      setShowModal(false)
    } catch (err: any) {
      setModalError(err.message || "Fejl under afsendelse.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Button variant="success" onClick={handleOpenModal}>
        Find produkter
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton className="border-0 pb-1">
          <Modal.Title className="fs-5 fw-semibold">Find produkter via PriceRunner</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 d-flex flex-column" style={{ height: "620px" }}>
          <div className="d-flex justify-content-between align-items-center bg-light border rounded px-3 py-2 mb-3">
            <small className="text-muted">
              Tilføj produktnavne manuelt. Indsend henter alle unikke matches fra PriceRunner.
            </small>
            <small className="fw-semibold text-secondary">
              Gyldige navne: {validNameCount}
            </small>
          </div>

          {modalError ? (
            <Alert variant="danger" className="py-2">
              {modalError}
            </Alert>
          ) : null}

          <div className="border rounded" style={{ height: "440px", overflowY: "auto", flexShrink: 0 }}>
            <Table size="sm" className="mb-0 align-middle">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th className="py-2" style={{ width: "88%" }}>Produktnavn</th>
                  <th className="py-2 text-center" style={{ width: "12%" }}>Handling</th>
                </tr>
              </thead>
              <tbody>
                {foundProducts.map((product, index) => (
                  <tr key={index}>
                    <td className="p-0" style={{ height: "44px" }}>
                      <Form.Control
                        type="text"
                        value={product.name}
                        placeholder="fx Apple MagSafe Charger 25W"
                        onChange={(e) => handleChangeProductName(index, e.target.value)}
                        className="h-100 border-0 rounded-0"
                        style={{ boxShadow: "none" }}
                        size="sm"
                      />
                    </td>
                    <td className="p-0" style={{ height: "44px" }}>
                      <Button
                        variant="danger"
                        className="w-100 h-100 border-0 rounded-0 fw-semibold"
                        onClick={() => handleDeleteProduct(index)}
                      >
                        Slet
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button variant="outline-primary" className="fw-semibold" onClick={handleAddProduct}>
              Tilføj række
            </Button>
            <Button
              variant="primary"
              className="fw-semibold px-4"
              onClick={handleSubmitProducts}
              disabled={isSubmitting || validNameCount === 0}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Indsender...
                </>
              ) : (
                "Indsend"
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}
